import { t as translate } from "../preferences";
import { Eye, EyeOff, LoaderCircle, LogIn, Wrench } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../api";
import { useAuth } from "../auth";
import { openExternal } from "../../telegram";

export function LoginScreen({ recovery = false }: { recovery?: boolean }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("+998");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (identifier.replace(/\D/g, "").length <= 3 || !password.trim()) {
      setError(translate("Telefon raqami va parolni kiriting"));
      return;
    }
    setPending(true);
    setError(null);
    try {
      await login(identifier, password);
      if (!recovery) navigate("/mechanic/", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (
          caught.code === "invalid_credentials" ||
          caught.status === 401 ||
          caught.status === 422
        ) {
          setError(translate("Telefon raqami yoki parol noto‘g‘ri"));
        } else if (caught.status === 429) {
          setError(translate("Juda ko‘p urinish. Birozdan keyin qayta urinib ko‘ring"));
        } else {
          setError(caught.message);
        }
      } else {
        setError(translate("Kutilmagan xatolik yuz berdi"));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="m-app-viewport m-app-safe-frame m-flex m-items-center m-justify-center m-bg-background m-px-4 m-py-8">
      <div className="m-w-full m-max-w-sm">
        <header className="m-mb-7 m-text-center">
          <span className="m-mx-auto m-flex m-size-16 m-items-center m-justify-center m-rounded-2xl m-bg-primary m-text-primary-foreground m-shadow-card">
            <Wrench className="m-size-8" aria-hidden="true" />
          </span>
          <p className="m-mt-5 m-text-xs m-font-bold m-uppercase m-tracking-[0.2em] m-text-primary">
            AvtoDaftar
          </p>
          <h1 className="m-mt-2 m-text-2xl m-font-bold m-tracking-tight m-text-foreground">
            {translate("Usta ilovasiga kirish")}</h1>
          <p className="m-mt-2 m-text-sm m-leading-6 m-text-muted">
            {translate("Ustaxona hisobingizdagi telefon va paroldan foydalaning.")}</p>
        </header>

        <form className="m-rounded-app m-border m-border-border m-bg-surface m-p-5 m-shadow-card" onSubmit={submit}>
          <label className="m-field-label" htmlFor="phone">{translate("Telefon raqami")}</label>
          <input
            id="phone"
            className="m-text-input"
            type="tel"
            autoComplete="username"
            inputMode="tel"
            value={identifier}
            disabled={pending}
            onChange={(event) => setIdentifier(event.target.value)}
          />

          <label className="m-field-label m-mt-4" htmlFor="password">{translate("Parol")}</label>
          <div className="m-relative">
            <input
              id="password"
              className="m-text-input m-pr-12"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              disabled={pending}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              type="button"
              className="m-absolute m-inset-y-0 m-right-0 m-flex m-w-12 m-items-center m-justify-center m-text-muted"
              aria-label={showPassword ? translate("Parolni yashirish") : translate("Parolni ko‘rsatish")}
              onClick={() => setShowPassword((show) => !show)}
            >
              {showPassword ? <EyeOff className="m-size-5" /> : <Eye className="m-size-5" />}
            </button>
          </div>

          {error ? (
            <p className="m-error-box m-mt-4" role="alert">
              {error}
            </p>
          ) : null}

          <button className="m-primary-button m-mt-5" type="submit" disabled={pending}>
            {pending ? <LoaderCircle className="m-size-5 m-animate-spin" /> : <LogIn className="m-size-5" />}
            {pending ? translate("Kirilmoqda…") : translate("Kirish")}
          </button>

          <div className="m-mt-5 m-grid m-grid-cols-2 m-gap-2 m-text-center m-text-sm m-font-semibold">
            <button type="button" className="m-link-button" onClick={() => openExternal("https://t.me/avtodaftar_bot?start=getaccess")}>{translate("Ruxsat olish")}</button>
            <button type="button" className="m-link-button" onClick={() => openExternal("https://t.me/avtodaftar_bot?start=resetpassword")}>{translate("Parolni tiklash")}</button>
          </div>
        </form>
      </div>
    </main>
  );
}
